import Accordion, { IAccordionItem } from "@/components/ui/accordion/accordion";
import TabGroup from "@/components/ui/tab-group/tab-group";
import { AnimatePresence, motion } from "framer-motion";
import { SectionLayout } from "layouts";
import { useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";

const KeyFeatures = () => {
  return (
    <SectionLayout
      separatorProps={{
        variant: "offset",
      }}
      heading={
        <Trans i18nKey="mhealth-studio.key-features.heading">
          Key <strong>features</strong>
        </Trans>
      }
    >
      <TabGroup
        labels={["mHealth studio app", "mHealth studio cloud"]}
        panels={[
          <Panel
            imagePath="app"
            accordionItems={[
              {
                title: "Central mHealth app platform",
                content: "Easy access to the hearX ® software",
              },
              {
                title: "Easy application management",
                content:
                  "Install new apps, check app versions and easily update to the latest versions",
              },
              {
                title: "Calibration management for headphones",
                content:
                  "Download of calibration profiles when linking calibrated headphones with device",
              },
              {
                title: "Offline testing mode",
                content:
                  "Once headphones are linked, device can be used without internet for testing and screening purposes",
              },
              {
                title: "Automatic data upload functionality",
                content:
                  "Data seamlessly synchronises to mHealth Studio Cloud when device is connected to internet",
              },
              {
                title: "Integrated data access",
                content:
                  "View test results in an online state, or local test results in an offline state",
              },
              {
                title: "Secure by design",
                content:
                  "Local data encryption at rest using AES-256 bit encryption. Secured server service via token authentication",
              },
              {
                title: "Data management",
                content: "Easily create patient and facility data on device",
              },
              {
                title: "Calibration statuses",
                content:
                  "Access the calibration status of the headphones and view the devices used to conduct tests",
              },
            ]}
          />,
          <Panel
            imagePath="cloud"
            accordionItems={[
              {
                title: "Secure, interoperable data monitoring",
                content:
                  "An EHR platform for real-time, online data management, surveillance, referrals and report generation",
              },
              {
                title: "Easy data management",
                content: "Centralised EHR management",
              },
              {
                title: "Detailed reports",
                content:
                  "Create and download patient and tester reports with smart filters, PDF and CSV options",
              },
              {
                title: "Event based SMS & email system",
                content:
                  "Send custom SMS & email upon test events at an additional cost",
              },
              {
                title: "Secure by design",
                content:
                  "Data is secured with 256-bit SSL encryption and is encrypted between hardware devices and the cloud platform",
              },
              {
                title: "Patient journey tracking",
                content:
                  "Ability to set statuses, track patient journeys and manage referrals",
              },
              {
                title: "Calibration statuses",
                content:
                  "Access the calibration statuses of the headphones and view the devices used to conduct tests",
              },
            ]}
          />,
        ]}
      />
    </SectionLayout>
  );
};

export default KeyFeatures;

const Panel: React.FC<{
  imagePath: string;
  accordionItems: IAccordionItem[];
}> = ({ imagePath, accordionItems }) => {
  const [accordionIndex, setAccordionIndex] = useState(0);
  const handleAccordionChange = useCallback((index: number | false) => {
    setAccordionIndex(+index);
  }, []);
  const appImage = useMemo(() => {
    return (
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        key={`accordion-image-${accordionItems[accordionIndex].title}`}
        src={`/assets/images/mhealth/key-features/${imagePath}/${accordionIndex}.png`}
        alt={accordionItems[accordionIndex].title}
        className="absolute inset-0 mx-auto"
      />
    );
  }, [accordionIndex]);
  return (
    <div className="grid mt-16 tablet:grid-cols-2">
      <div className="relative grid place-items-center min-h-[700px]">
        <AnimatePresence>{appImage}</AnimatePresence>
      </div>
      <Accordion onChange={handleAccordionChange} items={accordionItems} />
    </div>
  );
};
